/**
 * Percorso chat (ChatMutuo → Scheda Mutuo / Verifica Fattibilità → ritorno).
 *
 * Chi entra in Scheda o Verifica da un link della chat ha la chat come "base":
 * X, matita e back del browser fanno un passo indietro nella pila
 * (Verifica → Scheda → chat) e la chat, al rientro, si riposiziona sul
 * prodotto cliccato.
 *
 * Modulo CommonJS di funzioni pure (niente window/sessionStorage/navigate):
 * tutte le DECISIONI vivono qui e sono testate con node:test. Gli effetti
 * (sessionStorage, navigate) sono in src/utils/backNavigation.js.
 *
 * Il percorso viaggia nello state della cronologia (chatJourney, chatPath,
 * journeyDepth), che sopravvive a back/forward e ricarica. sessionStorage
 * contiene solo l'ancora del prodotto (JOURNEY_KEY) e la memoria dello
 * scroll della chat (SCROLL_KEY_PREFIX + superficie).
 */

const JOURNEY_KEY = "chatmutuo:journey";
const SCROLL_KEY_PREFIX = "chatmutuo:scroll:";
const JOURNEY_TTL_MS = 30 * 60 * 1000;
const SCROLL_BUTTON_THRESHOLD = 120;
const ORIGIN_PARAM = "origin";
const ORIGIN_VALUE = "chatmutuo";

function parseUrl(href) {
  if (!href) return null;
  try {
    return new URL(href, "http://x");
  } catch (_) {
    return null;
  }
}

// Link della chat che portano a Scheda/Verifica: il backend li emette sempre
// con ?ChatBotAction= (redirect gestito da FormRicercaMutuo).
function isChatProductLink(href) {
  const u = parseUrl(href);
  return !!u && u.searchParams.has("ChatBotAction");
}

function withChatOrigin(href) {
  const u = parseUrl(href);
  if (!u) return href;
  if (u.searchParams.get(ORIGIN_PARAM) === ORIGIN_VALUE) return href;
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}${ORIGIN_PARAM}=${ORIGIN_VALUE}`;
}

function normalizePath(p) {
  if (!p || p === "/") return "/";
  return p.endsWith("/") ? p.slice(0, -1) : p;
}

function buildJourneyEntryState(chatPath) {
  return {
    from: chatPath,
    chatPath,
    full: true,
    chatJourney: true,
    journeyDepth: 1,
  };
}

// Passo successivo dentro il percorso (es. Scheda → "Richiedi" → Verifica).
function buildJourneyStepState(currentState, fromPath) {
  if (!currentState?.chatJourney) return null;
  return {
    ...currentState,
    from: fromPath,
    full: true,
    journeyDepth: (currentState.journeyDepth || 1) + 1,
  };
}

// null = non siamo in un percorso chat: il chiamante usa la logica legacy.
function decideJourneyExit({ state, historyLength }) {
  if (!state?.chatJourney) return null;
  if (historyLength <= 1) {
    return { type: "navigate", to: state.chatPath, replace: true };
  }
  return { type: "back" };
}

// X / matita della Verifica FUORI dal percorso chat. Dalla Scheda ("Richiedi"
// fa push di /verifica/) si torna indietro di un passo invece di fare una
// nuova push di /mutuo/: così la X della Scheda torna poi ai risultati.
function decideVerificaExit({ from, step }) {
  if (step >= 2) return { type: "navigate", to: "/" };
  if (from && from.startsWith("/banche-mutui/")) {
    return { type: "navigate", to: from };
  }
  return { type: "back" };
}

// Scheda/Verifica senza prodotto in Redux (es. dopo una ricarica).
function decideMissingProductRedirect(state) {
  if (state?.chatJourney && state.chatPath) return state.chatPath;
  return "/";
}

function buildJourneyRecord({ surface, chatPath, anchorHref, anchorIndex, now }) {
  return { surface, chatPath, anchorHref, anchorIndex, ts: now };
}

function parseJourneyRecord(raw, now) {
  if (!raw) return null;
  try {
    const rec = JSON.parse(raw);
    if (!rec || !rec.ts || now - rec.ts > JOURNEY_TTL_MS) return null;
    return rec;
  } catch (_) {
    return null;
  }
}

// Rinnova la TTL di un record in sessionStorage (percorso chat o payload del
// widget) a ogni passo, così una Verifica lunga non perde ancora e widget.
function touchRecord(raw, now) {
  if (!raw) return null;
  try {
    const rec = JSON.parse(raw);
    if (!rec || typeof rec !== "object") return null;
    return JSON.stringify({ ...rec, ts: now });
  } catch (_) {
    return null;
  }
}

// Lo stesso prodotto può comparire in più risposte con href identico:
// salviamo quale occorrenza è stata cliccata.
function findAnchorIndex(candidates, clicked) {
  const i = Array.prototype.indexOf.call(candidates || [], clicked);
  return i < 0 ? 0 : i;
}

// Unico selettore per click e ripristino: link nei messaggi (<a href>) e
// bottoni CMS (data-chat-href) possono condividere lo stesso href.
// escapedHref: già passato da CSS.escape (browser).
function chatAnchorSelector(escapedHref) {
  return `a[href="${escapedHref}"], [data-chat-href="${escapedHref}"]`;
}

function decideChatRestore({ journey, surface, pathname, saved }) {
  if (
    journey?.anchorHref &&
    journey.surface === surface &&
    normalizePath(journey.chatPath) === normalizePath(pathname)
  ) {
    return {
      type: "anchor",
      href: journey.anchorHref,
      index: journey.anchorIndex || 0,
    };
  }
  if (saved) {
    if (saved.atBottom) return { type: "bottom" };
    return { type: "top", top: saved.top };
  }
  return { type: "default" };
}

function parseScrollMemory(raw) {
  if (!raw) return null;
  try {
    const mem = JSON.parse(raw);
    if (!mem || typeof mem.top !== "number" || !Number.isFinite(mem.top)) {
      return null;
    }
    return { top: mem.top, atBottom: !!mem.atBottom };
  } catch (_) {
    return null;
  }
}

function isAwayFromBottom(
  { scrollHeight, scrollTop, clientHeight },
  threshold = SCROLL_BUTTON_THRESHOLD
) {
  return scrollHeight - scrollTop - clientHeight > threshold;
}

// Il widget chiuso è display:none: metriche a zero che non vanno salvate.
function shouldPersistScroll({ clientHeight }) {
  return clientHeight > 0;
}

// Il bottone di invio fa anche da "vai all'ultimo messaggio": freccia verde in
// basso solo a campo vuoto e chat non in fondo. Con del testo invia sempre
// (inviare porta comunque giù la chat).
function decideSendButtonMode({ text, awayFromBottom }) {
  const hasText = !!(text && text.trim().length > 0);
  return !hasText && awayFromBottom ? "latest" : "send";
}

// Il ripristino va fatto sui messaggi già nel DOM: in Messages i dati Redux
// diventano renderedMessages (stato derivato) solo al render successivo.
function canRestoreChat({ renderedCount, clientHeight }) {
  return renderedCount > 0 && clientHeight > 0;
}

function mutuoCodeFromPath(pathname) {
  const m = /^\/mutuo\/([^/?#]+)\/?$/.exec(pathname || "");
  return m ? m[1] : null;
}

// La cronologia si carica una volta sola per documento (onClientEntry). Un
// documento nato prima della chat (bfcache dopo X/back, altro tab) ha lo store
// fermo a "chat mai iniziata": va risincronizzato se il cookie indica un'altra
// conversazione. Con lo stesso uid il fetch duplicherebbe i messaggi.
function shouldResyncHistory({ cookieUid, storeUid, loadingHistory, loading }) {
  if (!isValidChatUid(cookieUid) || loadingHistory || loading) return false;
  return cookieUid !== (storeUid || "");
}

// Il cookie chatmutuo-uid esiste solo se una conversazione è partita, ma un
// primo messaggio fallito lo scrive con null: universal-cookie lo salva come
// stringa "null" e in lettura non la riconverte.
function isValidChatUid(uid) {
  if (typeof uid !== "string") return false;
  const v = uid.trim();
  return v !== "" && v !== "null" && v !== "undefined";
}

module.exports = {
  JOURNEY_KEY,
  SCROLL_KEY_PREFIX,
  JOURNEY_TTL_MS,
  SCROLL_BUTTON_THRESHOLD,
  isChatProductLink,
  withChatOrigin,
  normalizePath,
  buildJourneyEntryState,
  buildJourneyStepState,
  decideJourneyExit,
  decideMissingProductRedirect,
  decideVerificaExit,
  buildJourneyRecord,
  parseJourneyRecord,
  touchRecord,
  findAnchorIndex,
  chatAnchorSelector,
  decideChatRestore,
  parseScrollMemory,
  isAwayFromBottom,
  shouldPersistScroll,
  decideSendButtonMode,
  canRestoreChat,
  mutuoCodeFromPath,
  shouldResyncHistory,
  isValidChatUid,
};
